import { useEffect, useMemo, useState } from "react";

import { Input, Modal, notification, Row, Table, Typography, Space } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ColumnsType } from "antd/es/table";
import Card from "../../countCard/Card";
import pageSize from "../../../constants/pageSize";

import {
  GlobalTableStyle,
  LightBlueContainer,
  WhiteContainer,
} from "../../styled/Container.styled";
import { DeleteButton, PrimaryButton, ViewDetailsButton, RefreshButton } from "../../styled/Button.styled";
import { useDeleteFareByIdMutation, useGetAllFareQuery } from "../../../feature/Fare/fareApiSlice";
import { IFare } from "../../../types/fare";
import styled from "styled-components";
import { categoryRank } from "../../../utils/categoryOrder";


const ViewChart = styled.div`
  color: dodgerblue;
`;


const Fare = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading,refetch} = useGetAllFareQuery();
  // Same order as the Categories page. Sorted copy: RTK Query hands back frozen data.
  const fareCategoryName = (fare: any): string | undefined =>
    fare?.localFareDTO?.name || fare?.outstationFares?.name || fare?.rentalChartList?.name;
  const sortedFares = useMemo(
    () =>
      [...(data?.data.content ?? [])].sort(
        (a: any, b: any) => categoryRank(fareCategoryName(a)) - categoryRank(fareCategoryName(b))
      ),
    [data]
  );
  const [deleteFareById] = useDeleteFareByIdMutation();
  const [nameValue, setNameValue] = useState("");
  const [localFare, setLocalFare] = useState("");
  const [outstationFare, setOutstationFare] = useState("");
  const { confirm } = Modal;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const showConfirm = (id: number) => {
    confirm({
      title: "Do You Want To Delete Fare?",
      onOk() {
        onDelete(id);
      },
      onCancel() { },
    });
  };

  const onDelete = (id: number) => {
    deleteFareById(id)
      .unwrap()
      .then(() => {
        notification.success({
          message: "Sucessfully Deleted!",
          placement: "bottomLeft",
        });
      });
  };

  const columns: ColumnsType<any> = [
    {
      key: "name",
      width: "22%",
      title: (
        <div>
          <Typography.Text> Category</Typography.Text>
          <Input.Search
            onChange={(e) => {
              setNameValue(e.target.value);
            }}
            placeholder="Search here"
          />
        </div>
      ),
      render: (value: any, record) => {
        return fareCategoryName(record);
      },
      dataIndex: "name",
    },
    {
      key: "baseFare",
      width: "22%",
      title: (
        <div>
          <Typography.Text>Local Fare/Per Km</Typography.Text>
          <Input.Search
            onChange={(e) => {
              setLocalFare(e.target.value);
            }}
            placeholder="Search here"
          />
        </div>
      ),
      render: (value: any, record) => {
        return record?.localFareDTO?.baseFare;

      },
      dataIndex: "baseFare",
    },
    {
      key: "outstationFare",
      width: "25%",
      title: (
        <div>
          <Typography.Text>Outstation Fare/Per Km</Typography.Text>
          <Input.Search
            onChange={(e) => {
              setOutstationFare(e.target.value);
            }}
            placeholder="Search here"
          />
        </div>
      ),
      render: (value: any, record) => {

        return record?.outstationFares?.baseFare;

      },
      dataIndex: "outstationFares",
    },

    {
      key: "",
      title: (
        <div>
          <Typography.Text>Rental</Typography.Text>
        </div>
      ),
      render: (_, record) => (
        !!record.rentalChartList &&
        <ViewChart
          onClick={() => openChart(record.id)}
          style={{ cursor: "pointer" }}
        >
          View Chart
        </ViewChart>
      ),
    },

    {
      key: "viewDetails",
      dataIndex: "viewDetails",
      render: (_, record) => (
        <ViewDetailsButton onClick={() => openBookingDetails(record.id)}>
          View Details
        </ViewDetailsButton>
      ),
    },
    {
      key: "deleteButton",
      width: "2%",
      render: (_, record) => (
        <DeleteButton onClick={() => showConfirm(record.id)} />
      ),
    },
  ];
  const openChart = (id: number) => {
    navigate(`edit/${id}?page=1`, { state: { tab: "3" } });
  };

  const openBookingDetails = (id: number) => {
    navigate(`edit/${id}?page=1`, {
      state: {
        fares: data?.data?.content
      }
    });
  };

  const sum = data?.data.content.length;

  useEffect(() => {
    refetch(); 
  }, [refetch]);

   useEffect(() => {
      // Restore page from URL when component mounts or when navigating back
      const urlParams = new URLSearchParams(routerLocation.search);
      const pageFromUrl = urlParams.get('page');
      if (pageFromUrl) {
        setCurrentPage(parseInt(pageFromUrl, 10));
      }
    }, [routerLocation.search]);

  return (
    <>
      <Row justify="space-between" className="button-bottom-margin">
        <Card sum={sum} text=" Fare" />
        <Space>
          <RefreshButton 
            loading={isRefreshing}
            onClick={async () => {
              setIsRefreshing(true);
              await refetch();
              setIsRefreshing(false);
            }} 
          />
          <Link to="add" state={{ fares: data?.data?.content }}>
            <PrimaryButton> Add Fare </PrimaryButton>
          </Link>
        </Space>
      </Row>
      <LightBlueContainer>
        <WhiteContainer>
          <GlobalTableStyle>
          <Table<IFare>
            className="transaction-table"
            loading={isLoading}
            columns={columns}
            dataSource={sortedFares}
            rowKey="id"
            pagination={{
              pageSize,
              current: currentPage,
              onChange: (page) => {
                setCurrentPage(page);
              },
            }}
          />
          </GlobalTableStyle>
       </WhiteContainer>
      </LightBlueContainer>
    </>
  );
};
export default Fare;